import React from "react";
import { MapPin, CheckCircle2, ShieldOff, ExternalLink } from "lucide-react";

const bucketStyle = {
  high: "bg-emerald-50 text-emerald-700 border-emerald-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-slate-100 text-slate-600 border-slate-200",
  unranked: "bg-slate-50 text-slate-400 border-slate-200",
};

const labelize = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export function PriorityBadge({ bucket, testid }) {
  return (
    <span data-testid={testid} className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold ${bucketStyle[bucket] || bucketStyle.unranked}`}>
      {bucket === "unranked" ? "Unranked" : labelize(bucket)}
    </span>
  );
}

/** Discovery result card (shared by Prospect List + Map bottom sheet). */
export function ProspectCard({ p, onAdd }) {
  return (
    <div data-testid={`prospect-card-${p.source_establishment_id}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-heading text-sm font-bold text-slate-800">{p.business_name}</p>
          <p className="mt-0.5 text-xs text-slate-500">
            {p.source_business_type}
            {p.venue_type && p.venue_type !== "other" ? ` · ${labelize(p.venue_type)}` : " · unmapped"}
          </p>
          <p className="mt-0.5 truncate text-xs text-slate-400">
            {[p.address_line_1, p.post_code].filter(Boolean).join(", ")}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <PriorityBadge bucket={p.fhrs_priority_bucket} testid={`prospect-bucket-${p.source_establishment_id}`} />
          <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">LeadCTRL Prospect Priority</span>
          <span className="mt-0.5 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-500" data-testid={`prospect-fsa-rating-${p.source_establishment_id}`}>
            FSA rating {p.source_rating_value ?? "—"}
          </span>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {p.is_known && (
          <span data-testid={`prospect-known-${p.source_establishment_id}`} className="inline-flex items-center gap-1 rounded-md bg-teal-50 px-2 py-0.5 text-xs font-semibold text-teal-700">
            <CheckCircle2 className="h-3 w-3" /> In your leads
          </span>
        )}
        {p.is_suppressed && (
          <span data-testid={`prospect-suppressed-${p.source_establishment_id}`} className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700">
            <ShieldOff className="h-3 w-3" /> Suppressed{p.suppression_reason ? ` · ${labelize(p.suppression_reason)}` : ""}
          </span>
        )}
      </div>

      <p className="mt-2 text-[11px] text-slate-400">
        Source: FSA FHRS · retrieved {p.retrieved_at ? new Date(p.retrieved_at).toLocaleDateString("en-GB") : "now"}
      </p>

      {!p.is_known && onAdd && (
        <button data-testid={`prospect-add-${p.source_establishment_id}`} onClick={() => onAdd(p)}
          className="mt-3 flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg bg-teal-700 text-sm font-semibold text-white hover:bg-teal-800">
          <MapPin className="h-4 w-4" /> Add to Leads
        </button>
      )}
    </div>
  );
}

export { labelize };
